/**
 * Blackjack rules shared by the table engine and the web client.
 *
 * Payouts and action gates match the legacy `blackjack/services/gameplay.js` table,
 * not a generic Vegas book: natural 3:2, split-ace 21 pays 1:1, dealer stands on
 * soft 17, no hole-card peek, up to three splits.
 */

export const BJ_SUITS = ["H", "D", "C", "S"] as const;
export const BJ_RANKS = [
  "2",
  "3",
  "4",
  "5",
  "6",
  "7",
  "8",
  "9",
  "10",
  "J",
  "Q",
  "K",
  "A",
] as const;

export type BjSuit = (typeof BJ_SUITS)[number];
export type BjRank = (typeof BJ_RANKS)[number];

export interface BjCard {
  suit: BjSuit;
  value: BjRank;
  hidden?: boolean;
}

/** What spectators see: a hole card has no rank until the dealer reveals. */
export type BjPublicCard = { hidden: true } | { suit: BjSuit; value: BjRank };

export const BJ = {
  DECKS_IN_SHOE: 6,
  IDLE_BETTING_MS: 45_000,
  IDLE_TURN_MS: 25_000,
  BONUS_AMOUNT: 2137,
  BONUS_COOLDOWN_MS: 2 * 60 * 60 * 1000,
  MIN_BET: 50,
  MAX_BET: 1_000_000,
  DEFAULT_BET: 50,
  STARTING_CHIPS: 1000,
  MAX_SEATS: 6,
  MAX_SPLITS: 3,
  RESHUFFLE_AT: 20,
  DEALER_REVEAL_MS: 1200,
  DEALER_HIT_MS: 900,
  ROUND_END_DELAY_MS: 3000,
  MAX_CHAT_LENGTH: 200,
  MAX_CHAT_HISTORY: 150,
} as const;

export const BJ_DEALER_NAMES = [
  "Mietek",
  "Rysiek",
  "Zbyszek",
  "Grażyna",
  "Halina",
  "Bogdan",
  "Zdzichu",
  "Jadzia",
  "Krycha",
  "Waldek",
  "Wiesiek",
  "Franek",
  "Staszek",
  "Irena",
  "Basia",
  "Marek",
  "Romek",
  "Ela",
] as const;

export type BjPhase = "waiting" | "betting" | "playing" | "dealer";
export type BjAction = "hit" | "stand" | "double" | "split" | "surrender" | "insurance";
export type BjHandStatus = "active" | "split_ace" | "surrender";
export type BjHandResult = "win" | "lose" | "push" | "blackjack" | "busted" | "surrender";

export interface BjTurn {
  userId: number;
  handIndex: number;
}

export interface BjHandOutcome {
  result: BjHandResult;
  winAmount: number;
  playerValue: number;
  dealerValue: number;
  blackjack: boolean;
  /** Chips to add at settlement. Surrender already refunded half at action time. */
  chipsDelta: number;
  insuranceDelta?: number;
}

export interface BjPublicPlayer {
  userId: number;
  name: string;
  /** Stable table slot 0–5 so the oval does not reshuffle when someone leaves. */
  seatIndex: number;
  hands: BjPublicCard[][];
  bets: number[];
  chips: number;
  status: string;
  splitDone: boolean;
  splitCount: number;
  insuranceBet: number;
  hasBet: boolean;
  hasActedOnHand: boolean[];
  handStatus: BjHandStatus[];
  /** Equipped card theme. Other seats render this player's cards with it. */
  skinId: string;
  keys: number;
}

export interface BjWaitingEntry {
  userId: number;
  name: string;
  chips: number;
  keys: number;
}

export interface BjDealerResult {
  value: number;
  blackjack: boolean;
  busted: boolean;
}

export interface BjGameUpdate {
  dealerHand: BjPublicCard[];
  phase: BjPhase;
  dealerResult: BjDealerResult | null;
  roundResults: Record<string, BjHandOutcome>;
  currentTurn: BjTurn | null;
  insuranceOpen: boolean;
  players: Record<string, BjPublicPlayer>;
  waitingList: BjWaitingEntry[];
  dealerName: string;
  seatedCount: number;
  bettingEndsAt: number | null;
  turnEndsAt: number | null;
}

export interface BjChatMessage {
  user: string;
  text: string;
  ts: number;
  cid?: string;
  role?: "dealer" | "player";
  isDealer?: boolean;
}

export interface BjWallet {
  chips: number;
  keys: number;
  equippedSkin: string;
  canClaimBonus: boolean;
  nextBonusAt: number | null;
  bonusAmount: number;
}

export interface BjLeaderboardRow {
  username: string;
  maxChips: number;
}

export interface BjBonusStatus {
  canClaim: boolean;
  nextAvailableAt: number | null;
  amount: number;
  chips: number;
  keys: number;
  equippedSkin: string;
}

export function suitGlyph(suit: BjSuit): string {
  if (suit === "H") return "♥";
  if (suit === "D") return "♦";
  if (suit === "C") return "♣";
  return "♠";
}

export function suitColor(suit: BjSuit): "red" | "black" {
  return suit === "H" || suit === "D" ? "red" : "black";
}

export function isTenVal(rank: string): boolean {
  return rank === "10" || rank === "J" || rank === "Q" || rank === "K";
}

export function rankValue(rank: string): number {
  if (rank === "A") return 11;
  if (rank === "K" || rank === "Q" || rank === "J") return 10;
  return Number.parseInt(rank, 10) || 0;
}

/** Soft-total: aces count as 11 unless that busts. Hidden cards are ignored. */
export function handValue(hand: readonly BjCard[]): number {
  let value = 0;
  let aces = 0;
  for (const card of hand) {
    if (card.hidden) continue;
    if (card.value === "A") {
      aces += 1;
      value += 11;
    } else {
      value += rankValue(card.value);
    }
  }
  while (value > 21 && aces > 0) {
    value -= 10;
    aces -= 1;
  }
  return value;
}

export function isNaturalBlackjack(hand: readonly BjCard[]): boolean {
  const visible = hand.filter((card) => !card.hidden);
  return visible.length === 2 && handValue(visible) === 21;
}

export function publicCard(card: BjCard): BjPublicCard {
  if (card.hidden) return { hidden: true };
  return { suit: card.suit, value: card.value };
}

export function publicDealerHand(hand: readonly BjCard[], phase: BjPhase): BjPublicCard[] {
  if (phase === "playing") {
    return hand.map((card) => publicCard(card));
  }
  return hand.map((card) => ({ suit: card.suit, value: card.value }));
}

export interface BjActionContext {
  phase: BjPhase;
  isYourTurn: boolean;
  hand: readonly BjCard[];
  handStatus: BjHandStatus;
  hasActed: boolean;
  chips: number;
  bet: number;
  splitCount: number;
  insuranceOpen: boolean;
  insuranceBet: number;
  primaryBet: number;
}

export function canHit(ctx: BjActionContext): boolean {
  if (!ctx.isYourTurn || ctx.phase !== "playing") return false;
  if (ctx.handStatus === "surrender" || ctx.handStatus === "split_ace") return false;
  return handValue(ctx.hand) < 21;
}

export function canStand(ctx: BjActionContext): boolean {
  if (!ctx.isYourTurn || ctx.phase !== "playing") return false;
  return ctx.handStatus !== "surrender";
}

function isUnsplitNatural(ctx: BjActionContext): boolean {
  return ctx.splitCount === 0 && ctx.handStatus === "active" && isNaturalBlackjack(ctx.hand);
}

export function canDouble(ctx: BjActionContext): boolean {
  if (!ctx.isYourTurn || ctx.phase !== "playing") return false;
  if (ctx.handStatus === "surrender") return false;
  if (isUnsplitNatural(ctx)) return false;
  return ctx.hand.length === 2 && ctx.chips >= ctx.bet && ctx.bet > 0;
}

export function canSplit(ctx: BjActionContext): boolean {
  if (!ctx.isYourTurn || ctx.phase !== "playing") return false;
  if (ctx.handStatus === "surrender" || ctx.hasActed) return false;
  if (ctx.hand.length !== 2 || ctx.chips < ctx.bet || ctx.bet <= 0) return false;
  if (ctx.splitCount >= BJ.MAX_SPLITS) return false;
  const first = ctx.hand[0];
  const second = ctx.hand[1];
  if (!first || !second) return false;
  return first.value === second.value || (isTenVal(first.value) && isTenVal(second.value));
}

export function canSurrender(ctx: BjActionContext): boolean {
  if (!ctx.isYourTurn || ctx.phase !== "playing") return false;
  if (ctx.handStatus !== "active") return false;
  if (isUnsplitNatural(ctx)) return false;
  return ctx.hand.length === 2 && !ctx.hasActed;
}

export function canInsurance(ctx: BjActionContext): boolean {
  if (!ctx.isYourTurn || ctx.phase !== "playing") return false;
  if (!ctx.insuranceOpen || ctx.insuranceBet > 0) return false;
  const stake = Math.floor(ctx.primaryBet / 2);
  return stake > 0 && ctx.chips >= stake;
}

export function allowedActions(ctx: BjActionContext): BjAction[] {
  const actions: BjAction[] = [];
  if (canHit(ctx)) actions.push("hit");
  if (canStand(ctx)) actions.push("stand");
  if (canDouble(ctx)) actions.push("double");
  if (canSplit(ctx)) actions.push("split");
  if (canInsurance(ctx)) actions.push("insurance");
  if (canSurrender(ctx)) actions.push("surrender");
  return actions;
}

export function insuranceStake(primaryBet: number): number {
  return Math.floor(primaryBet / 2);
}

/** Display delta after the stake was already taken: +2:1 net or −stake. */
export function insuranceDisplayDelta(insuranceBet: number, dealerBlackjack: boolean): number {
  return dealerBlackjack ? insuranceBet * 2 : -insuranceBet;
}

/** Chips added at settlement. Stake was deducted when the player bought insurance. */
export function insuranceChipsDelta(insuranceBet: number, dealerBlackjack: boolean): number {
  return dealerBlackjack ? insuranceBet * 3 : 0;
}

export function settleHand(
  hand: readonly BjCard[],
  bet: number,
  status: BjHandStatus,
  dealerValue: number,
  dealerBlackjack: boolean,
): BjHandOutcome {
  const playerValue = handValue(hand);
  const blackjack = hand.length === 2 && playerValue === 21;
  const splitAceTwentyOne = status === "split_ace" && playerValue === 21;

  if (status === "surrender") {
    return {
      result: "surrender",
      winAmount: Math.floor(bet / 2),
      playerValue,
      dealerValue,
      blackjack,
      chipsDelta: 0,
    };
  }

  if (playerValue > 21) {
    return {
      result: "busted",
      winAmount: 0,
      playerValue,
      dealerValue,
      blackjack,
      chipsDelta: 0,
    };
  }

  if (dealerBlackjack && blackjack) {
    return {
      result: "push",
      winAmount: bet,
      playerValue,
      dealerValue,
      blackjack,
      chipsDelta: bet,
    };
  }

  if (dealerBlackjack) {
    return {
      result: "lose",
      winAmount: 0,
      playerValue,
      dealerValue,
      blackjack,
      chipsDelta: 0,
    };
  }

  if (blackjack || splitAceTwentyOne) {
    if (splitAceTwentyOne) {
      return {
        result: "blackjack",
        winAmount: bet,
        playerValue,
        dealerValue,
        blackjack,
        chipsDelta: bet * 2,
      };
    }
    const winAmount = Math.floor(bet * 1.5);
    return {
      result: "blackjack",
      winAmount,
      playerValue,
      dealerValue,
      blackjack,
      chipsDelta: bet + winAmount,
    };
  }

  if (dealerValue > 21 || playerValue > dealerValue) {
    return {
      result: "win",
      winAmount: bet,
      playerValue,
      dealerValue,
      blackjack,
      chipsDelta: bet * 2,
    };
  }

  if (playerValue === dealerValue) {
    return {
      result: "push",
      winAmount: bet,
      playerValue,
      dealerValue,
      blackjack,
      chipsDelta: bet,
    };
  }

  return {
    result: "lose",
    winAmount: 0,
    playerValue,
    dealerValue,
    blackjack,
    chipsDelta: 0,
  };
}

/** Natural, split-ace 21, or a blackjack that pushed against a dealer blackjack. */
export function handAwardsKey(outcome: Pick<BjHandOutcome, "result" | "blackjack">): boolean {
  return outcome.result === "blackjack" || (outcome.result === "push" && outcome.blackjack);
}

export function createShoe(decks = BJ.DECKS_IN_SHOE, random = Math.random): BjCard[] {
  const shoe: BjCard[] = [];
  for (let i = 0; i < decks; i++) {
    for (const suit of BJ_SUITS) {
      for (const value of BJ_RANKS) {
        shoe.push({ suit, value });
      }
    }
  }
  return shuffleCards(shoe, random);
}

export function shuffleCards(deck: BjCard[], random = Math.random): BjCard[] {
  const next = deck.slice();
  for (let i = next.length - 1; i > 0; i--) {
    const j = Math.floor(random() * (i + 1));
    const a = next[i];
    const b = next[j];
    if (a && b) {
      next[i] = b;
      next[j] = a;
    }
  }
  return next;
}

export function pickDealerName(current?: string, random = Math.random): string {
  const pool = BJ_DEALER_NAMES.filter((name) => name !== current);
  const source = pool.length > 0 ? pool : BJ_DEALER_NAMES;
  const index = Math.floor(random() * source.length);
  return source[index] ?? "Mietek";
}

export function clampBet(amount: number, chips: number): number {
  if (!Number.isFinite(amount)) return 0;
  const whole = Math.floor(amount);
  if (whole < BJ.MIN_BET) return 0;
  return Math.min(whole, BJ.MAX_BET, chips);
}

export function roundResultKey(userId: number, handIndex: number): string {
  return `${userId}-${handIndex}`;
}

export function bonusReady(lastBonusAtMs: number | null, now = Date.now()): boolean {
  if (lastBonusAtMs === null) return true;
  return now - lastBonusAtMs >= BJ.BONUS_COOLDOWN_MS;
}

export function nextBonusAt(lastBonusAtMs: number | null, now = Date.now()): number | null {
  if (lastBonusAtMs === null) return null;
  const next = lastBonusAtMs + BJ.BONUS_COOLDOWN_MS;
  return next > now ? next : null;
}
